import React from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import { Box, ButtonBase, Menu, MenuItem, Typography } from "@mui/material";

import { SUPPORTED_LANGUAGES } from "../i18n/catalog";
import { LanguageContext } from "../utils/LanguageProvider";

const LANGUAGE_LABEL_KEYS = {
  es: "es",
  en: "en",
  no: "no_idioma",
  jp: "jp",
  zh: "zh",
  fr: "fr",
  ar: "ar",
  ru: "ru",
  pt: "pt",
};

const DEFAULT_NATIVE_LABELS = {
  es: "Español",
  en: "English",
  no: "Noruego",
  jp: "Japonés",
  zh: "Chino Mandarín",
  fr: "Français",
  ar: "العربية",
  ru: "Русский",
  pt: "Português",
};

const getTranslatedLabel = (translate, code) => {
  const key = LANGUAGE_LABEL_KEYS[code] || code;
  const translated = typeof translate === "function" ? translate(key) : "";

  if (translated && translated !== key) {
    return translated;
  }

  return DEFAULT_NATIVE_LABELS[code] || code.toUpperCase();
};

export default function LanguageSelector({ sx, translate }) {
  const { language, changeLanguage } = React.useContext(LanguageContext);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const languages = React.useMemo(
    () =>
      SUPPORTED_LANGUAGES.map((code) => ({
        code,
        nativeLabel: getTranslatedLabel(translate, code),
      })),
    [translate]
  );

  const open = Boolean(anchorEl);
  const activeLanguage =
    languages.find((item) => item.code === language) ?? languages[0];

  const languageLabelRaw =
    typeof translate === "function" ? translate("language") : "";
  const languageLabel =
    languageLabelRaw && languageLabelRaw !== "language"
      ? languageLabelRaw
      : "Language";

  const selectLanguageRaw =
    typeof translate === "function" ? translate("select_language") : "";
  const selectLanguageLabel =
    selectLanguageRaw && selectLanguageRaw !== "select_language"
      ? selectLanguageRaw
      : languageLabel;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (nextLanguage) => {
    changeLanguage(nextLanguage);
    handleClose();
  };

  return (
    <>
      <ButtonBase
        id="service-language-selector-trigger"
        aria-label={selectLanguageLabel}
        aria-controls={open ? "service-language-selector-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="menu"
        onClick={handleOpen}
        sx={{
          minWidth: { xs: "7vw", sm: "6vw" },
          minHeight: "4vh",
          px: { xs: "0.5vw", sm: "0.4vw" },
          py: "0.3vh",
          borderRadius: "0.9vh",
          border: "1px solid rgba(232, 79, 14, 0.22)",
          background:
            "linear-gradient(180deg, rgba(17, 28, 43, 0.96), rgba(10, 17, 29, 0.96))",
          boxShadow: open
            ? "0 0 0 1px rgba(232, 79, 14, 0.18), 0 1vh 1.5vw rgba(0, 0, 0, 0.28)"
            : "0 0.5vh 1vw rgba(0, 0, 0, 0.22)",
          display: "flex",
          alignItems: "center",
          gap: "0.4vw",
          justifyContent: "space-between",
          color: "#f5f5f5",
          transition:
            "transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease, background 140ms ease",
          backdropFilter: "blur(0.4vw)",
          "&:hover": {
            transform: "translateY(-0.05vh)",
            borderColor: "rgba(232, 79, 14, 0.45)",
            background:
              "linear-gradient(180deg, rgba(20, 34, 52, 0.98), rgba(11, 19, 32, 0.98))",
          },
          ...sx,
        }}
      >
        <Box
          sx={{
            width: "1vw",
            height: "1vw",
            minWidth: "1vw",
            minHeight: "1vw",
            borderRadius: "0.4vh",
            display: "grid",
            placeItems: "center",
            background:
              "linear-gradient(180deg, rgba(232, 79, 14, 0.18), rgba(232, 79, 14, 0.06))",
            color: "#f4f6f8",
            flexShrink: 0,
          }}
        >
          <TranslateRoundedIcon sx={{ fontSize: "0.55vw" }} />
        </Box>

        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            alignItems: "flex-start",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.425vw",
              lineHeight: 1.1,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(195, 195, 195, 0.7)",
            }}
          >
            {languageLabel}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.575vw",
              lineHeight: 1.15,
              fontWeight: 700,
              color: "#f7f7f7",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {activeLanguage?.nativeLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            px: { xs: "0.4vw", sm: "0.35vw" },
            py: "0.15vh",
            borderRadius: "999vh",
            background: "rgba(91, 157, 214, 0.14)",
            border: "1px solid rgba(91, 157, 214, 0.18)",
            fontSize: "0.4vw",
            fontWeight: 800,
            letterSpacing: "0.08em",
            color: "#d9edf9",
            flexShrink: 0,
          }}
        >
          {activeLanguage?.code?.toUpperCase()}
        </Box>

        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: "0.6vw",
            color: "rgba(245, 245, 245, 0.82)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 140ms ease",
            flexShrink: 0,
          }}
        />
      </ButtonBase>

      <Menu
        id="service-language-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ zIndex: 50000 }}
        MenuListProps={{
          "aria-labelledby": "service-language-selector-trigger",
          sx: {
            p: "0.4vh",
            display: "grid",
            gap: "0.25vh",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              zIndex: 50000,
              mt: "0.4vh",
              minWidth: "7vw",
              borderRadius: "0.9vh",
              border: "1px solid rgba(232, 79, 14, 0.3)",
              background:
                "linear-gradient(180deg, rgba(16, 24, 38, 1), rgba(10, 16, 27, 1))",
              boxShadow:
                "0 1.5vh 1.5vw rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(232, 79, 14, 0.08)",
              backdropFilter: "blur(0.6vw)",
              overflow: "hidden",
            },
          },
        }}
      >
        {languages.map((item) => {
          const isActive = item.code === language;

          return (
            <MenuItem
              key={item.code}
              selected={isActive}
              onClick={() => handleSelect(item.code)}
              sx={{
                minHeight: "2.7vh",
                px: "0.45vw",
                py: "0.45vh",
                borderRadius: "0.7vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.4vw",
                color: "#f2f5f8",
                background: "rgba(255, 255, 255, 0.015)",
                transition:
                  "background 120ms ease, transform 120ms ease, border-color 120ms ease",
                border: "1px solid transparent",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, rgba(232, 79, 14, 0.18), rgba(255, 255, 255, 0.05))",
                  borderColor: "rgba(232, 79, 14, 0.18)",
                },
                "&.Mui-selected": {
                  background:
                    "linear-gradient(90deg, rgba(232, 79, 14, 0.22), rgba(57, 87, 116, 0.38))",
                  borderColor: "rgba(232, 79, 14, 0.28)",
                },
                "&.Mui-selected:hover": {
                  background:
                    "linear-gradient(90deg, rgba(232, 79, 14, 0.24), rgba(57, 87, 116, 0.42))",
                  borderColor: "rgba(232, 79, 14, 0.34)",
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.475vw",
                    fontWeight: isActive ? 800 : 600,
                    lineHeight: 1.1,
                    color: "#ffffff",
                    textShadow: "0 0.05vh 0.05vw rgba(0, 0, 0, 0.28)",
                  }}
                >
                  {item.nativeLabel}
                </Typography>
                <Typography
                  sx={{
                    mt: "0.1vh",
                    fontSize: "0.34vw",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(220, 228, 236, 0.82)",
                  }}
                >
                  {item.code}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "0.7vw",
                  height: "0.7vw",
                  borderRadius: "999vh",
                  display: "grid",
                  placeItems: "center",
                  background: isActive
                    ? "rgba(232, 79, 14, 0.34)"
                    : "rgba(255, 255, 255, 0.08)",
                  color: isActive ? "#fff1e8" : "rgba(195, 195, 195, 0.45)",
                  flexShrink: 0,
                }}
              >
                {isActive ? (
                  <CheckRoundedIcon sx={{ fontSize: "0.475vw" }} />
                ) : null}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
// import React from "react";
// import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
// import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
// import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
// import { Box, ButtonBase, Menu, MenuItem, Typography } from "@mui/material";

// import { SUPPORTED_LANGUAGES } from "../i18n/catalog";
// import { LanguageContext } from "../utils/LanguageProvider";

// const LANGUAGE_LABEL_KEYS = {
//   es: "es",
//   en: "en",
//   no: "no_idioma",
//   jp: "jp",
//   zh: "zh",
//   fr: "fr",
//   ar: "ar",
//   ru: "ru",
//   pt: "pt",
// };

// const DEFAULT_NATIVE_LABELS = {
//   es: "Español",
//   en: "English",
//   no: "Noruego",
//   jp: "Japonés",
//   zh: "Chino Mandarín",
//   fr: "Français",
//   ar: "العربية",
//   ru: "Русский",
//   pt: "Português",
// };

// const getTranslatedLabel = (translate, code) => {
//   const key = LANGUAGE_LABEL_KEYS[code] || code;
//   const translated = typeof translate === "function" ? translate(key) : "";

//   if (translated && translated !== key) {
//     return translated;
//   }

//   return DEFAULT_NATIVE_LABELS[code] || code.toUpperCase();
// };

// export default function LanguageSelector({ sx, translate }) {
//   const { language, changeLanguage } = React.useContext(LanguageContext);
//   const [anchorEl, setAnchorEl] = React.useState(null);

//   const languages = React.useMemo(
//     () =>
//       SUPPORTED_LANGUAGES.map((code) => ({
//         code,
//         nativeLabel: getTranslatedLabel(translate, code),
//       })),
//     [translate]
//   );

//   const open = Boolean(anchorEl);
//   const activeLanguage =
//     languages.find((item) => item.code === language) ?? languages[0];

//   const languageLabelRaw =
//     typeof translate === "function" ? translate("language") : "";
//   const languageLabel =
//     languageLabelRaw && languageLabelRaw !== "language"
//       ? languageLabelRaw
//       : "Language";

//   const selectLanguageRaw =
//     typeof translate === "function" ? translate("select_language") : "";
//   const selectLanguageLabel =
//     selectLanguageRaw && selectLanguageRaw !== "select_language"
//       ? selectLanguageRaw
//       : languageLabel;

//   const handleOpen = (event) => {
//     setAnchorEl(event.currentTarget);
//   };

//   const handleClose = () => {
//     setAnchorEl(null);
//   };

//   const handleSelect = (nextLanguage) => {
//     changeLanguage(nextLanguage);
//     handleClose();
//   };

//   return (
//     <>
//       <ButtonBase
//         id="service-language-selector-trigger"
//         aria-label={selectLanguageLabel}
//         aria-controls={open ? "service-language-selector-menu" : undefined}
//         aria-expanded={open ? "true" : undefined}
//         aria-haspopup="menu"
//         onClick={handleOpen}
//         sx={{
//           minWidth: { xs: "3.3rem", sm: "11.5rem" },
//           minHeight: "2.75rem",
//           px: { xs: 0.9, sm: 1.1 },
//           py: 0.45,
//           borderRadius: "1vw",
//           border: "0.05vw solid rgba(232, 79, 14, 0.22)",
//           background:
//             "linear-gradient(180deg, rgba(17, 28, 43, 0.96), rgba(10, 17, 29, 0.96))",
//           boxShadow: open
//             ? "0 0 0 1px rgba(232, 79, 14, 0.18), 0 0.9rem 2rem rgba(0, 0, 0, 0.28)"
//             : "0 0.45rem 1.4rem rgba(0, 0, 0, 0.22)",
//           display: "flex",
//           alignItems: "center",
//           gap: 0.9,
//           justifyContent: "space-between",
//           color: "#f5f5f5",
//           transition:
//             "transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease, background 140ms ease",
//           backdropFilter: "blur(8px)",
//           "&:hover": {
//             transform: "translateY(-1px)",
//             borderColor: "rgba(232, 79, 14, 0.45)",
//             background:
//               "linear-gradient(180deg, rgba(20, 34, 52, 0.98), rgba(11, 19, 32, 0.98))",
//           },
//           ...sx,
//         }}
//       >
//         <Box
//           sx={{
//             width: "1.9rem",
//             height: "1.9rem",
//             borderRadius: "0.72rem",
//             display: "grid",
//             placeItems: "center",
//             background:
//               "linear-gradient(180deg, rgba(232, 79, 14, 0.18), rgba(232, 79, 14, 0.06))",
//             color: "#f4f6f8",
//             flexShrink: 0,
//           }}
//         >
//           <TranslateRoundedIcon sx={{ fontSize: "0.7vw" }} />
//         </Box>

//         <Box
//           sx={{
//             display: { xs: "none", sm: "flex" },
//             flexDirection: "column",
//             alignItems: "flex-start",
//             minWidth: 0,
//             flex: 1,
//           }}
//         >
//           <Typography
//             sx={{
//               fontSize: "0.4vw",
//               lineHeight: 1.1,
//               letterSpacing: "0.08em",
//               textTransform: "uppercase",
//               color: "rgba(195, 195, 195, 0.7)",
//             }}
//           >
//             {languageLabel}
//           </Typography>
//           <Typography
//             sx={{
//               fontSize: "0.6vw",
//               lineHeight: 1.15,
//               fontWeight: 700,
//               color: "#f7f7f7",
//               whiteSpace: "nowrap",
//               overflow: "hidden",
//               textOverflow: "ellipsis",
//             }}
//           >
//             {activeLanguage?.nativeLabel}
//           </Typography>
//         </Box>

//         <Box
//           sx={{
//             px: { xs: 0.6, sm: 0.7 },
//             py: 0.22,
//             borderRadius: "9vw",
//             background: "rgba(91, 157, 214, 0.14)",
//             border: "1px solid rgba(91, 157, 214, 0.18)",
//             fontSize: "0.45vw",
//             fontWeight: 800,
//             letterSpacing: "0.08vw",
//             color: "#d9edf9",
//             flexShrink: 0,
//           }}
//         >
//           {activeLanguage?.code?.toUpperCase()}
//         </Box>

//         <KeyboardArrowDownRoundedIcon
//           sx={{
//             fontSize: ".85vw",
//             color: "rgba(245, 245, 245, 0.82)",
//             transform: open ? "rotate(180deg)" : "rotate(0deg)",
//             transition: "transform 140ms ease",
//             flexShrink: 0,
//           }}
//         />
//       </ButtonBase>

//       <Menu
//         id="service-language-selector-menu"
//         anchorEl={anchorEl}
//         open={open}
//         onClose={handleClose}
//         sx={{ zIndex: 50000 }}
//         MenuListProps={{
//           "aria-labelledby": "service-language-selector-trigger",
//           sx: {
//             p: 0.75,
//             display: "grid",
//             gap: 0.45,
//           },
//         }}
//         anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         transformOrigin={{ vertical: "top", horizontal: "right" }}
//         slotProps={{
//           paper: {
//             sx: {
//               zIndex: 50000,
//               mt: 0.7,
//               minWidth: "8vw",
//               borderRadius: "0.5vw",
//               border: "0.1vw solid rgba(232, 79, 14, 0.3)",
//               background:
//                 "linear-gradient(180deg, rgba(16, 24, 38, 1), rgba(10, 16, 27, 1))",
//               boxShadow:
//                 "0 1.4rem 2.8rem rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(232, 79, 14, 0.08)",
//               backdropFilter: "blur(22px)",
//               overflow: "hidden",
//             },
//           },
//         }}
//       >
//         {languages.map((item) => {
//           const isActive = item.code === language;

//           return (
//             <MenuItem
//               key={item.code}
//               selected={isActive}
//               onClick={() => handleSelect(item.code)}
//               sx={{
//                 minHeight: "3rem",
//                 px: 1.15,
//                 py: 0.8,
//                 borderRadius: "0.8rem",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "space-between",
//                 gap: 1,
//                 color: "#f2f5f8",
//                 background: "rgba(255, 255, 255, 0.015)",
//                 transition:
//                   "background 120ms ease, transform 120ms ease, border-color 120ms ease",
//                 border: "1px solid transparent",
//                 "&:hover": {
//                   background:
//                     "linear-gradient(90deg, rgba(232, 79, 14, 0.18), rgba(255, 255, 255, 0.05))",
//                   borderColor: "rgba(232, 79, 14, 0.18)",
//                 },
//                 "&.Mui-selected": {
//                   background:
//                     "linear-gradient(90deg, rgba(232, 79, 14, 0.22), rgba(57, 87, 116, 0.38))",
//                   borderColor: "rgba(232, 79, 14, 0.28)",
//                 },
//                 "&.Mui-selected:hover": {
//                   background:
//                     "linear-gradient(90deg, rgba(232, 79, 14, 0.24), rgba(57, 87, 116, 0.42))",
//                   borderColor: "rgba(232, 79, 14, 0.34)",
//                 },
//               }}
//             >
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   sx={{
//                     fontSize: "0.96rem",
//                     fontWeight: isActive ? 800 : 600,
//                     lineHeight: 1.1,
//                     color: "#ffffff",
//                     textShadow: "0 1px 1px rgba(0, 0, 0, 0.28)",
//                   }}
//                 >
//                   {item.nativeLabel}
//                 </Typography>
//                 <Typography
//                   sx={{
//                     mt: 0.18,
//                     fontSize: "0.68rem",
//                     letterSpacing: "0.08em",
//                     textTransform: "uppercase",
//                     color: "rgba(220, 228, 236, 0.82)",
//                   }}
//                 >
//                   {item.code}
//                 </Typography>
//               </Box>

//               <Box
//                 sx={{
//                   width: "1.35rem",
//                   height: "1.35rem",
//                   borderRadius: "999px",
//                   display: "grid",
//                   placeItems: "center",
//                   background: isActive
//                     ? "rgba(232, 79, 14, 0.34)"
//                     : "rgba(255, 255, 255, 0.08)",
//                   color: isActive ? "#fff1e8" : "rgba(195, 195, 195, 0.45)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {isActive ? (
//                   <CheckRoundedIcon sx={{ fontSize: "0.95rem" }} />
//                 ) : null}
//               </Box>
//             </MenuItem>
//           );
//         })}
//       </Menu>
//     </>
//   );
// }
